/**
 * Dynamic medical PA form layout: loads order, clinical data, form config, and NCCN docs;
 * renders section nav + {@link MedicalPaForm} with save/continue footer.
 *
 * Data loading, side effects, and the save handler live in
 * {@link useMedicalPaFormLayout}; this component only composes the header,
 * content, and footer around that state.
 *
 * @module medicalPaForm/medicalPaFormLayout
 */
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import MedicalPaFormContent from "./components/medicalPaFormContent";
import MedicalPaFormFooter from "./components/medicalPaFormFooter";
import MedicalPaFormHeader from "./components/medicalPaFormHeader";
import useMedicalPaFormLayout from "./hooks/useMedicalPaFormLayout";

/**
 * Page shell for the configurable medical PA questionnaire tied to the route `id`.
 *
 * @returns Loading state or the form chrome with footer actions
 */
const MedicalPaFormLayout = () => {
  const {
    id,
    isPageLoading,
    activeSectionId,
    setActiveSectionId,
    generalOrderDetails,
    authOnFileDetails,
    submissionData,
    guidelinesDocuments,
    healthCareFacilityData,
    singleMedicalPaFormConfig,
    latestMedicalPaFormData,
    workflowConfig,
    isSaving,
    loading,
    isAllFieldsValidationFree,
    isLoading,
    hasQuestions,
    shouldRunMedicalNeccessity,
    navigate,
    updateSingleOrderIdWithFormData,
  } = useMedicalPaFormLayout();

  return (
    <div className="medical-pharma-pa-form__container flex h-full flex-col bg-white">
      {isPageLoading ? (
        <div className="flex h-full items-center justify-center">
          <LoaderMessage message="Fetching form details..." />
        </div>
      ) : (
        <>
          <div className="medical-pharma-pa-form__body flex flex-1 flex-col overflow-hidden rounded bg-white px-4 pb-0 pt-2">
            <MedicalPaFormHeader
              generalOrderDetails={generalOrderDetails}
              authOnFileDetails={authOnFileDetails}
              submissionData={submissionData}
              guidelinesDocuments={guidelinesDocuments}
              healthCareFacilityData={healthCareFacilityData}
            />
            <MedicalPaFormContent
              id={id ?? ""}
              singleMedicalPaFormConfig={singleMedicalPaFormConfig}
              latestMedicalPaFormData={latestMedicalPaFormData}
              activeSectionId={activeSectionId}
              setActiveSectionId={setActiveSectionId}
            />
          </div>
          <MedicalPaFormFooter
            isSaving={isSaving}
            loading={loading}
            isAllFieldsValidationFree={isAllFieldsValidationFree}
            isLoading={isLoading}
            submissionType={workflowConfig?.submission_type}
            hasQuestions={hasQuestions}
            questionnaireStatus={
              generalOrderDetails?.order?.status?.questionnaire_status
            }
            onSave={() => updateSingleOrderIdWithFormData(true)}
            onSubmit={() => updateSingleOrderIdWithFormData(false)}
            onToggleMedicalNeccessity={(value) =>
              (shouldRunMedicalNeccessity.current = value)
            }
            onGoBack={() => navigate(`/worklists/singleOrder/${id}`)}
          />
        </>
      )}
    </div>
  );
};

export default MedicalPaFormLayout;
